import {z} from 'zod';
import {compoundConditionOutputSchema} from '../../promptToCardAgentV2.schemas';
import {CompoundConditionCardScratchpadState} from '../../scratchpad/compoundConditionCardScratchpad.types';

/**
 * Validates the structure of a compound condition output.
 * Ensures all sections have at least one rule and all rules have required fields.
 * @param output - The output to validate (raw schema input)
 * @returns An error string if validation fails, empty string if valid
 */
export default function validateCompoundConditionOutput(
  output: z.infer<typeof compoundConditionOutputSchema>,
  scratchpad: CompoundConditionCardScratchpadState['card'],
): string {
  const outputPointer = `The output is not valid.`;

  if (!output.name?.trim()) {
    return `${outputPointer} name is required`;
  }

  if (!output.sections?.length) {
    return `${outputPointer} at least one section is required`;
  }

  for (let sectionIdx = 0; sectionIdx < output.sections.length; sectionIdx++) {
    const section = output.sections[sectionIdx];

    if (!section.rules?.length) {
      return `${outputPointer} section ${sectionIdx + 1} must have at least one rule`;
    }

    for (let ruleIdx = 0; ruleIdx < section.rules.length; ruleIdx++) {
      const rule = section.rules[ruleIdx];

      if (!rule.signal?.value) {
        return `${outputPointer} rule ${ruleIdx + 1} in section ${sectionIdx + 1} is missing a signal`;
      }

      if (rule.ruleOperator === undefined || rule.ruleOperator === null) {
        return `${outputPointer} rule ${ruleIdx + 1} in section ${sectionIdx + 1} is missing an operator`;
      }

      const signal = scratchpad.availableSignals?.find(
        (s) =>
          s.value === rule.signal.value &&
          s.categories.length === (rule.signal.categories?.length || 0) &&
          s.categories.every((cat, idx) => cat === (rule.signal.categories?.[idx] || null)),
      );

      if (!signal) {
        return `${outputPointer} signal ${rule.signal.name} with value ${rule.signal.value} and categories ${rule.signal.categories?.join(', ')} not found in available signals. Available signals are: ${scratchpad.availableSignals?.map((s) => s.name).join(', ')}`;
      }

      if (!signal.optionalOperators.includes(rule.ruleOperator)) {
        return `${outputPointer} operator ${rule.ruleOperator} is not allowed for the signal ${rule.signal.name}. Allowed operators are: ${signal.optionalOperators.join(', ')}`;
      }

      if (signal.strictOptionalValues) {
        const optionalValues = signal.optionalValues.map((o) => o.value);
        const invalidValues = rule.ruleValue?.textValues?.filter(
          (value) => !optionalValues.includes(value),
        );

        if (invalidValues?.length) {
          return `${outputPointer} provided value ${invalidValues?.join(', ')} is not optional for the signal ${rule.signal.name}. Optional values are: ${optionalValues.join(', ')}`;
        }
      }
    }
  }

  return '';
}
